import React from 'react';
import isEmpty from 'lodash/isEmpty';
import isObject from 'lodash/isObject';
import { useShallow } from 'zustand/react/shallow';
import {
  DateFilter,
  FilterDefinition,
  FilterUsageOption,
  GraphQLFilter,
  AndFilter,
  SearchFilter,
  PermissionFilterOption,
} from '../@types';
import { isSearchFilter, applyModifier } from '../utils/filterUtils';
import filterContext, { IFilterContext } from '../contexts/filterContext';
import { parseContextFilters } from '../providers/FilterProvider/FilterProvider';
import useFilterPersistenceStore from '../providers/FilterProvider/useFilterPersistenceStore';

export interface FilterHookResult
  extends Omit<IFilterContext, 'filters' | 'updateDateFilterPersistance'> {
  filters: Record<string, FilterDefinition | FilterDefinition[]>;
  getPermissionFilter: (
    filter: GraphQLFilter,
    modifier?: Array<string | PermissionFilterOption>,
  ) => GraphQLFilter;
  getGraphQLFilter: (
    modifiers?: Array<FilterUsageOption>,
  ) => GraphQLFilter | undefined;
  getDateFilter: () => DateFilter;
  upsertFilter: (
    key: string,
    value: FilterDefinition | Array<FilterDefinition>,
  ) => void;
  removeFilter: (key: string) => void;
  setContext: (
    value: Record<string, FilterDefinition | Array<FilterDefinition>>,
  ) => void;
}

export const useQueryFilter = (
  keys?: Array<string>,
  partitionId: 'default' | string | undefined = 'default',
): FilterHookResult => {
  const {
    filters,
    setFilters,
    updateDateFilterPersistance,
    dateFilterMode,
    persist,
  } = React.useContext(filterContext);

  const { getFilters, setFilters: setPersistedFilters } =
    useFilterPersistenceStore(
      useShallow((state) => ({
        getFilters: state.getFilters,
        setFilters: state.setFilters,
      })),
    );

  // Filter the filters object based on the array of keys
  const reqFilters = React.useMemo(() => {
    const partitionFilters =
      partitionId === 'default'
        ? filters.default
        : { ...filters.default, ...filters[partitionId] };
    if (!keys) return partitionFilters;
    return Object.fromEntries(
      Object.entries(partitionFilters).filter(([key]) => keys.includes(key)),
    );
  }, [keys, filters, partitionId]);

  /**
   * Load persisted filters on mount if persist is true
   * Use ref to ensure it only loads once per partitionId
   */
  const hasLoadedPersistedFilters = React.useRef<string>('');

  React.useEffect(() => {
    if (!persist) return;

    if (hasLoadedPersistedFilters.current === partitionId) {
      return;
    }

    const persistedFilters = getFilters(partitionId);
    if (!persistedFilters || Object.keys(persistedFilters).length === 0) {
      hasLoadedPersistedFilters.current = partitionId;
      return;
    }

    // Filter out context filters (ctx_*) and createdAt from persisted filters
    const filteredPersistedFilters = Object.fromEntries(
      Object.entries(persistedFilters).filter(
        ([key]) => !key.startsWith('ctx_') && key !== 'createdAt',
      ),
    );

    if (Object.keys(filteredPersistedFilters).length === 0) {
      hasLoadedPersistedFilters.current = partitionId;
      return;
    }

    // Merge persisted filters with existing filters, giving priority to persisted ones
    setFilters((oldFilters) => {
      const currentPartitionFilters =
        partitionId === 'default'
          ? oldFilters.default || {}
          : {
              ...(oldFilters.default || {}),
              ...(oldFilters[partitionId] || {}),
            };

      // Merge: persisted filters have priority, but don't override context filters or createdAt
      const mergedFilters = {
        ...currentPartitionFilters,
        ...filteredPersistedFilters,
      };

      return {
        ...oldFilters,
        [partitionId]: mergedFilters,
      };
    });

    hasLoadedPersistedFilters.current = partitionId;
  }, [persist, partitionId, getFilters, setFilters]);

  /**
   * Save filters to persistence store when they change
   * Save all filters that are being used in the current partitionId
   * (including default filters when partitionId is not 'default')
   */
  React.useEffect(() => {
    if (!persist) {
      return undefined;
    }

    // Get all filters being used in this partition (same logic as reqFilters but without keys filtering)
    const currentPartitionFilters =
      partitionId === 'default'
        ? filters.default
        : { ...filters.default, ...(filters[partitionId] || {}) };

    // Debounce function to avoid too many writes
    const timeoutId = setTimeout(() => {
      // Filter out context filters (ctx_*) and createdAt from filters to persist
      const filtersToPersist = Object.fromEntries(
        Object.entries(currentPartitionFilters).filter(
          ([key, value]) =>
            !key.startsWith('ctx_') &&
            key !== 'createdAt' &&
            value !== undefined &&
            value !== null &&
            (Array.isArray(value) ? value.length > 0 : true) &&
            (!isObject(value) ||
              Object.keys(value).length > 0 ||
              (isObject(value) &&
                'or' in value &&
                Array.isArray((value as SearchFilter).or) &&
                (value as SearchFilter).or.length > 0)),
        ),
      );

      // Only save if there are filters to persist
      if (Object.keys(filtersToPersist).length > 0) {
        setPersistedFilters(partitionId, filtersToPersist);
      } else {
        // If no filters to persist, clear the persisted filters for this pageId
        useFilterPersistenceStore.getState().clearFilters(partitionId);
      }
    }, 300);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [filters, partitionId, persist, setPersistedFilters]);

  /**
   * Sets the context filters
   */
  const setContext: FilterHookResult['setContext'] = React.useCallback(
    (value) => {
      const result = parseContextFilters(value);

      setFilters((oldFilters) => ({
        ...oldFilters,
        [partitionId]: { ...oldFilters[partitionId], ...result },
      }));
    },
    [setFilters, partitionId],
  );

  /**
   * Inserts or updates a filter by its key
   */
  const upsertFilter: FilterHookResult['upsertFilter'] = React.useCallback(
    (key, value) => {
      if (partitionId === 'default' && key === 'createdAt') {
        updateDateFilterPersistance(value as DateFilter);
      }

      setFilters((oldFilters) => ({
        ...oldFilters,
        [partitionId]: { ...oldFilters[partitionId], [key]: value },
      }));
    },
    [setFilters, partitionId, updateDateFilterPersistance],
  );

  /**
   * Removes a filter by its key
   */
  const removeFilter: FilterHookResult['removeFilter'] = React.useCallback(
    (key) =>
      setFilters((oldFilters) => {
        if (!oldFilters || !oldFilters[partitionId]) {
          return oldFilters;
        }

        return {
          ...oldFilters,
          [partitionId]: Object.fromEntries(
            Object.entries(oldFilters[partitionId]).filter(([k]) => k !== key),
          ),
        };
      }),
    [setFilters, partitionId],
  );

  /**
   * Parses the filters to be used in the apollo
   *
   * Implementation logic:
   * - If filter is a search filter, just add it to the `and` key
   * - If filter is not an array, check:
   *   - Key already exists in the output? append the entry to the `and` key
   *   - Key not already defined in the output? add it to the output directly
   * - Otherwise, create an object with `or` key with all rules and put it inside the `and` key
   */
  const getGraphQLFilter: FilterHookResult['getGraphQLFilter'] =
    React.useCallback(
      (filterModifiers) => {
        const result = Object.entries(reqFilters)
          .filter(([, value]) => {
            if (!value) return false;
            if (Array.isArray(value) && value.length > 0) return true;
            return Object.entries(value).length > 0;
          })
          // Reduces to an object which applies an AND logic
          .reduce((acc, [key, value]) => {
            // Search filter
            if (isSearchFilter(key) && isObject(value)) {
              acc.and = [
                ...(Array.isArray(acc.and) ? acc.and : []),
                {
                  or: (value as SearchFilter).or.map((obj) => {
                    const [entry] = Object.entries(obj);
                    // Apply modifier if needed
                    const modifier = filterModifiers?.find(
                      (m) => m.id === entry[0],
                    );
                    const [k, v] = applyModifier(
                      entry[0],
                      entry[1],
                      modifier?.modifier,
                    );
                    return { [k]: v };
                  }),
                },
              ] as AndFilter<FilterDefinition>['and'];
              return acc;
            }

            // Apply modifier if needed
            const modifier = filterModifiers?.find((m) => m.id === key);
            if (!Array.isArray(value)) {
              const [k, m] = applyModifier(key, value, modifier?.modifier);

              // In case the filter should override any existing filter with the same name
              if (modifier?.override) {
                acc[k] = m;
                return acc;
              }

              if (acc[k]) {
                acc.and = [
                  ...(Array.isArray(acc.and) ? acc.and : []),
                  { [k]: m } as Record<string, FilterDefinition>,
                ] as AndFilter<FilterDefinition>['and'];
              } else {
                acc[k] = m;
              }
              return acc;
            }

            // Other filters (arrays)
            const arrayFilter = {
              or: value.map((v) => {
                const [k, m] = applyModifier(key, v, modifier?.modifier);
                return { [k]: m } as Record<string, FilterDefinition>;
              }),
            };

            // In case the filter should override any existing filter with the same name
            if (
              modifier?.override &&
              Array.isArray(acc.and) &&
              acc.and.length > 0
            ) {
              // Search for this filter in the acc.and array
              const index = acc.and.findIndex((fd) => {
                if ('or' in fd) {
                  return (
                    fd.or?.[0]?.[modifier?.modifier?.key || key] !== undefined
                  );
                }
                return false;
              });
              if (index >= 0) {
                acc[index] = arrayFilter;
                return acc;
              }
            }

            acc.and = [
              ...(Array.isArray(acc.and) ? acc.and : []),
              { ...arrayFilter },
            ] as AndFilter<FilterDefinition>['and'];
            return acc;
          }, {} as GraphQLFilter);
        return !isEmpty(result) ? result : undefined;
      },
      [reqFilters],
    );

  /**
   * Filters and applies modifier to permissionFilter object
   */
  const getPermissionFilter: FilterHookResult['getPermissionFilter'] =
    React.useCallback((filter, modifier) => {
      return (
        modifier?.reduce((acc, perm) => {
          if (typeof perm === 'string') {
            if (filter[perm]) {
              acc[perm] = filter[perm];
            }
          } else if (filter[perm.id]) {
            // Goes through applyModifier so a dotted key reaches a nested
            // filter — `'floor.unitId'` becomes `{ floor: { unitId } }`, which
            // is the only way to restrict a resource the query holds one
            // relation away.
            const [key, value] = applyModifier(
              perm.id,
              filter[perm.id] as FilterDefinition,
              perm.modifier,
            );
            acc[key] = value;
          }
          return acc;
        }, {} as GraphQLFilter) || {}
      );
    }, []);

  /**
   * Retrieves the page date filter value
   */
  const getDateFilter: FilterHookResult['getDateFilter'] = React.useCallback(
    () => (reqFilters?.createdAt || filters.default.createdAt) as DateFilter,
    [reqFilters, filters.default],
  );

  return {
    filters: reqFilters,
    setFilters,
    upsertFilter,
    removeFilter,
    getPermissionFilter,
    getGraphQLFilter,
    getDateFilter,
    setContext,
    dateFilterMode,
  };
};
